/**
 * QG Trust Badges — click-to-learn-more disclosure interaction.
 * Spec (binding): theme/docs/ground-up/trust-badges-round3.md, "FINAL
 * APPROVAL" section (M1 fade-up reveal · M2 hover lift · M3 touch feedback ·
 * M4 anchored popover desktop / bottom sheet mobile, never a new tab · M5/M6
 * not used on this badge row). Disclosure panels are server-rendered with a
 * native `hidden` attribute (see qg-trust-badges.liquid) — this script only
 * handles positioning, open/close, and focus management. Reuses the theme's
 * global `trapFocus`/`removeTrapFocus` (global.js, loaded before this file).
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 640px)';
  var openState = null; // { trigger, panel, scrim, onKey, onScrim, mode }

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function focusables(el) {
    return Array.prototype.slice.call(
      el.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  function closeDisclosure() {
    if (!openState) return;
    var state = openState;
    openState = null;

    document.removeEventListener('keydown', state.onKey, true);
    if (state.scrim) {
      state.scrim.removeEventListener('mousedown', state.onScrim);
      if (state.scrim.parentNode) state.scrim.parentNode.removeChild(state.scrim);
    }

    state.panel.hidden = true;
    state.panel.setAttribute('aria-modal', 'false');
    state.panel.classList.remove(
      'qg-trust-badges__panel--popover',
      'qg-trust-badges__panel--sheet',
      'qg-trust-badges__panel--below'
    );
    state.panel.style.top = '';
    state.panel.style.left = '';
    state.trigger.setAttribute('aria-expanded', 'false');

    if (window.removeTrapFocus) {
      window.removeTrapFocus(state.trigger);
    } else if (typeof state.trigger.focus === 'function') {
      state.trigger.focus();
    }
  }

  function bindPanel(panel) {
    var closeBtn = panel.querySelector('[data-qg-trust-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeDisclosure);

    var onKey = function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDisclosure();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return onKey;
  }

  function trapAndFocus(panel) {
    var focusable = focusables(panel);
    if (window.trapFocus) {
      window.trapFocus(panel, focusable[0] || panel);
    } else if (focusable[0]) {
      focusable[0].focus();
    }
  }

  function positionPopover(panel, trigger) {
    panel.style.visibility = 'hidden';
    panel.hidden = false;

    var r = trigger.getBoundingClientRect();
    var sx = window.pageXOffset;
    var sy = window.pageYOffset;
    var vw = document.documentElement.clientWidth;
    var pw = panel.offsetWidth;
    var ph = panel.offsetHeight;

    var left = r.left + sx + r.width / 2 - pw / 2;
    left = Math.max(sx + 12, Math.min(left, sx + vw - pw - 12));

    var top;
    if (r.bottom + ph + 14 < window.innerHeight || r.top < ph + 20) {
      top = r.bottom + sy + 10;
    } else {
      top = r.top + sy - ph - 10;
      panel.classList.add('qg-trust-badges__panel--below');
    }

    panel.style.top = top + 'px';
    panel.style.left = left + 'px';
    panel.style.visibility = '';
  }

  function openPopover(trigger, panel) {
    panel.classList.add('qg-trust-badges__panel--popover');
    positionPopover(panel, trigger);

    var onKey = bindPanel(panel);
    trigger.setAttribute('aria-expanded', 'true');
    trapAndFocus(panel);

    openState = { trigger: trigger, panel: panel, scrim: null, onScrim: null, onKey: onKey, mode: 'popover' };
  }

  function openSheet(trigger, panel) {
    var scrim = document.createElement('div');
    scrim.className = 'qg-trust-badges__scrim';
    document.body.appendChild(scrim);

    panel.classList.add('qg-trust-badges__panel--sheet');
    panel.hidden = false;
    panel.setAttribute('aria-modal', 'true');

    var onScrim = function () {
      closeDisclosure();
    };
    scrim.addEventListener('mousedown', onScrim);

    var onKey = bindPanel(panel);
    trigger.setAttribute('aria-expanded', 'true');
    trapAndFocus(panel);

    openState = { trigger: trigger, panel: panel, scrim: scrim, onScrim: onScrim, onKey: onKey, mode: 'sheet' };
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-qg-trust-trigger]');
    if (!trigger) return;
    event.preventDefault();

    var panelId = trigger.getAttribute('aria-controls');
    var panel = panelId && document.getElementById(panelId);
    if (!panel) return;

    if (openState && openState.trigger === trigger) {
      closeDisclosure();
      return;
    }
    closeDisclosure();

    if (isMobile()) {
      openSheet(trigger, panel);
    } else {
      openPopover(trigger, panel);
    }
  });

  window.addEventListener('resize', function () {
    if (openState) closeDisclosure();
  });
  window.addEventListener(
    'scroll',
    function () {
      if (openState && openState.mode === 'popover') closeDisclosure();
    },
    true
  );

  // M1 — staggered fade-up reveal, once, when the row scrolls into view.
  // Content is present in the DOM without JS; this only adds the class that
  // triggers the CSS keyframe (assets/qg-sections.css). No-ops cleanly under
  // prefers-reduced-motion (the class add is harmless; the CSS keyframe is
  // suppressed by the media query either way).
  var prefersReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var rows = document.querySelectorAll('[data-qg-trust-row]');
  if (rows.length && 'IntersectionObserver' in window && !prefersReduce) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('qg-trust-badges__row--play');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.35 }
    );
    rows.forEach(function (row) {
      io.observe(row);
    });
  } else {
    rows.forEach(function (row) {
      row.classList.add('qg-trust-badges__row--play');
    });
  }
})();
